import React, { useCallback, useMemo } from 'react';
import { Box, Grid, Option, OptionKey, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { EMPLOYEE_STATUS_OPTIONS } from '@/modules/employee/employee.constants';

import {
  ACCOUNT_STATUS_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
  ACTIVITY_DATE_RANGE_FOR_OPTIONS,
  CARD_STATUSES_OPTIONS,
  CIP_STATUS_OPTIONS,
  CLAIM_STATUSES_OPTIONS,
  CLAIM_TYPE_OPTIONS,
  ENROLLMENT_STATUS_OPTIONS,
  PLAN_YEAR_OPTIONS,
} from '../../report.types';
import { LAST_30_DAYS_KEY } from '../../ReportDetails/CardStatusReport/CardStatusReport.types';
import { ScheduledItemFilters } from '../ScheduledReport.types';

import OrganizationTitle from './OrganizationTitle';
import ScheduledFilterItem from './ScheduledFilterItem';
interface Props extends Partial<ScheduledItemFilters> {
  testId?: string;
  isWithSSN?: boolean;
}

interface GetItemParams {
  testId: string;
  options: Option[];
  values?: OptionKey[];
  value?: OptionKey | null;
  title: string;
  isEmptyAllowed?: boolean;
}
const ScheduledReportItemFilters: React.FC<Props> = ({
  isWithSSN = false,
  employerId,
  isSSNShowed,
  cardStatusReportGroupType,
  isNotMailed,
  cardStatuses,
  accountStatuses,
  accountTypes,
  employmentStatuses,
  enrollmentStatuses,
  cipStatuses,
  activityDateRangeFor,
  claimStatuses,
  claimTypes,
  invoiceId,
  planYearPeriod,
  planNames,
  testId,
  asDate,
  employerIds,
}) => {
  const cardStatusReportTypeNode = useMemo(() => {
    if (cardStatusReportGroupType) {
      let currentText = 'Last 7 days';
      if (cardStatusReportGroupType === LAST_30_DAYS_KEY) {
        currentText = 'Last 30 days';
      }
      return <ScheduledFilterItem title="Card Status Report Group Type" text={currentText} />;
    }
    return null;
  }, [cardStatusReportGroupType]);

  const getItems = useCallback(
    (params: GetItemParams) => {
      if (params.value) {
        const found = params.options.find((option) => option.key === params.value);
        const title = found?.title || (found?.value as string);
        return (
          <ScheduledFilterItem
            title={params.title}
            testId={`${testId}_item_${params.testId}`}
            text={title || ''}
          />
        );
      }
      if (!params.values) {
        return null;
      }
      let text = 'All';
      if (params.isEmptyAllowed && params.values) {
        if (!_.isEmpty(params.values)) {
          text = params.values.join(', ');
        }
      } else if (params.values?.length === params.options.length - 1 || params.values?.length === 0) {
        //
      } else {
        const formattedList = params.values.map((key) => {
          const found = params.options.find((option) => option.key === key);
          return found?.title || found?.value;
        });

        const compactedList = _.compact(formattedList);
        text = compactedList.join(', ');
      }
      return (
        <ScheduledFilterItem title={params.title} testId={`${testId}_item_${params.testId}`} text={text} />
      );
    },
    [testId]
  );
  const cardStatusesNode = useMemo(() => {
    return getItems({
      options: CARD_STATUSES_OPTIONS,
      title: 'Card Statuses',
      values: cardStatuses,
      testId: 'card-status',
    });
  }, [cardStatuses, getItems]);
  const accountStatusesNode = useMemo(() => {
    return getItems({
      options: ACCOUNT_STATUS_OPTIONS,
      title: 'Account Statuses',
      values: accountStatuses,
      testId: 'account-status',
    });
  }, [accountStatuses, getItems]);
  const accountTypesNode = useMemo(() => {
    return getItems({
      options: ACCOUNT_TYPE_OPTIONS,
      title: 'Account Types',
      values: accountTypes,
      testId: 'account-types',
    });
  }, [accountTypes, getItems]);
  const activityDateRangeForNode = useMemo(() => {
    return getItems({
      options: ACTIVITY_DATE_RANGE_FOR_OPTIONS,
      title: 'Date range for',
      value: activityDateRangeFor,
      testId: 'date range for',
    });
  }, [activityDateRangeFor, getItems]);
  const employmentStatusesNode = useMemo(() => {
    return getItems({
      options: EMPLOYEE_STATUS_OPTIONS,
      title: 'Employment Statuses',
      values: employmentStatuses,
      testId: 'employment-status',
    });
  }, [employmentStatuses, getItems]);

  const enrollmentStatusesNode = useMemo(() => {
    return getItems({
      options: ENROLLMENT_STATUS_OPTIONS,
      title: 'Enrollment Statuses',
      values: enrollmentStatuses,
      testId: 'enrollment-status',
    });
  }, [enrollmentStatuses, getItems]);

  const cipStatusesNode = useMemo(() => {
    return getItems({
      options: CIP_STATUS_OPTIONS,
      title: 'CIP Statuses',
      values: cipStatuses,
      testId: 'cip-status',
    });
  }, [cipStatuses, getItems]);
  const claimStatusesNode = useMemo(() => {
    return getItems({
      options: CLAIM_STATUSES_OPTIONS,
      title: 'Claims Statuses',
      values: claimStatuses,
      testId: 'claim-status',
    });
  }, [claimStatuses, getItems]);
  const claimTypesNode = useMemo(() => {
    return getItems({
      options: CLAIM_TYPE_OPTIONS,
      title: 'Claim Types',
      values: claimTypes,
      testId: 'claim-types',
    });
  }, [claimTypes, getItems]);
  const planYearPeriodsNode = useMemo(() => {
    return getItems({
      options: PLAN_YEAR_OPTIONS,
      title: 'Plan Year Periods',
      values: planYearPeriod,
      testId: 'plan-year-period',
    });
  }, [getItems, planYearPeriod]);

  const plansNode = useMemo(() => {
    return getItems({
      options: [],
      title: 'Plans',
      values: planNames,
      testId: 'plan-ids',
      isEmptyAllowed: true,
    });
  }, [getItems, planNames]);

  return (
    <Box direction="column" fill pad="spacing16" round="container2Round" background="module">
      <Box
        data-testid={testId}
        round="container2Round"
        background="canvas"
        pad="spacing16"
        border={{ size: 'small', color: 'border2' }}
        direction="column"
      >
        <Text size="medium" weight={700}>
          Schedule filters
        </Text>
        <Box direction="column" fill margin={{ top: 'spacing12' }}>
          <Grid columns="1/4" gap="spacing12">
            <OrganizationTitle employerId={employerId} employerIds={employerIds} testId={testId} />
            {/* <ScheduledFilterItem
              title="Employer"
              testId={`${testId}_employer-name`}
              isLoading={isOrganizationLoading}
              text={organization.name}
            /> */}

            {isWithSSN && (
              <ScheduledFilterItem testId={`${testId}_ssn`} title="SSN">
                {isSSNShowed ? 'Show Full SSN' : 'Last 4 digits'}
              </ScheduledFilterItem>
            )}
            {cardStatusReportTypeNode}
            {isNotMailed ||
              (isNotMailed === false && (
                <ScheduledFilterItem testId={`${testId}_ssn`} title="Is not mailed">
                  {isNotMailed ? 'True' : 'False'}
                </ScheduledFilterItem>
              ))}
            {invoiceId && (
              <ScheduledFilterItem testId={`${testId}_invoice-id`} title="Invoice ID" text={invoiceId} />
            )}
            {plansNode}
            {activityDateRangeForNode}
            {cardStatusesNode}
            {accountStatusesNode}
            {accountTypesNode}
            {employmentStatusesNode}
            {enrollmentStatusesNode}
            {cipStatusesNode}
            {claimStatusesNode}
            {claimTypesNode}
            {planYearPeriodsNode}
            {asDate && (
              <ScheduledFilterItem testId={`${testId}_date`} title="As of date">
                {dayjs(asDate).format(DEFAULT_DATE_FORMAT)}
              </ScheduledFilterItem>
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
export default ScheduledReportItemFilters;
